import { HardDriveUpload, Library } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import type { FileSourceMode } from './types';
import { useFileLibraryFeatureEnabled } from './useFileLibraryFeature';

interface FileSourceChooserProps {
  value: FileSourceMode;
  onChange: (value: FileSourceMode) => void;
  disabled?: boolean;
  className?: string;
}

export const FileSourceChooser = ({
  value,
  onChange,
  disabled,
  className,
}: FileSourceChooserProps) => {
  const { t } = useTranslation('fileLibrary');
  const libraryEnabled = useFileLibraryFeatureEnabled();
  const options: Array<{ value: FileSourceMode; icon: typeof HardDriveUpload; label: string }> = [
    { value: 'local', icon: HardDriveUpload, label: t('efs.file_library.source.local') },
    ...(libraryEnabled
      ? [{ value: 'library' as const, icon: Library, label: t('efs.file_library.source.library') }]
      : []),
  ];

  return (
    <div
      className={cn(
        'grid w-full min-w-0 max-w-full rounded-md border border-efs-border bg-efs-bg-subtle p-1',
        options.length > 1 ? 'grid-cols-2' : 'grid-cols-1',
        className,
      )}
      role="group"
      aria-label={t('efs.file_library.source.label')}
    >
      {options.map((option) => {
        const Icon = option.icon;
        const selected = value === option.value;
        return (
          <Button
            key={option.value}
            type="button"
            variant={selected ? 'default' : 'ghost'}
            size="sm"
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className="w-full min-w-0 justify-center gap-1.5 px-2 text-center leading-tight whitespace-normal"
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="min-w-0 break-words">{option.label}</span>
          </Button>
        );
      })}
    </div>
  );
};
